import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  TextField,
  useTheme,
  MenuItem,
  Select,
  OutlinedInput,
  FormControl,
  Chip,
  LinearProgress,
  CircularProgress
} from '@mui/material';

import { getLowDevices, getAllDevices, getOrdersFromCSP } from 'API/Stock';

export default function DashboardCards() {
  const theme = useTheme();
  const [fromDate, setFromDate] = useState('2025-09-01');
  const [toDate, setToDate] = useState('2025-09-15');
  const [selectedOption, setSelectedOption] = useState('stock');
  const [lowDeviceData, setLowDeviceData] = useState([]);
  const [deviceList, setDeviceList] = useState([]);
  const [orderData, setOrderData] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [allDevicesMap, setAllDevicesMap] = useState({});

  // Fetch order data
  const fetchOrderData = async (from, to, productIds = [], deviceName = null) => {
    setLoadingOrders(true);
    try {
      console.log("Calling getOrdersFromCSP with:", { fromDate: from, toDate: to, productIds, deviceName });
      const data = await getOrdersFromCSP(from, to, productIds, deviceName);

      setTimeout(() => {
        if (data?.error === 'No orders found') {
          // Handle the specific "No orders found" response
          setOrderData([{ name: 'No Orders Found', count: '-' }]);
        } else if (data) {
          // Map normal order data
          const mappedData = [
            { name: 'Total Order', count: data.total_orders },
            { name: 'Order Received', count: data.orders_received },
            { name: 'Order Shipped', count: data.orders_shipped },
            { name: 'Order Delivered', count: data.orders_delivered }
          ];
          setOrderData(mappedData);
        } else {
          // No data received
          setOrderData([{ name: 'No Orders Found', count: '-' }]);
        }

        setLoadingOrders(false);
      }, 500);

      return data;
    } catch (err) {
      console.error('Error fetching order data:', err);
      setOrderData([{ name: 'No Orders Found', count: '-' }]);
      setLoadingOrders(false);
      return null;
    }
  };




  // Handle dropdown change
  const handleChange = async (event) => {
    const selectedDevice = event.target.value;
    setSelectedOption(selectedDevice);

    // If "stock" (all devices) is selected, pass no filter
    if (selectedDevice === 'stock') {
      console.log("Selected ALL devices");
      const data = await fetchOrderData(fromDate, toDate, [], null);
      console.log("API Output for ALL:", data);
      return;
    }

    const ids = allDevicesMap[selectedDevice] || [];
    console.log("Device selected:", selectedDevice);
    console.log("Product IDs to send:", ids);

    // Pass selected device (device_name) along with product IDs
    const data = await fetchOrderData(fromDate, toDate, ids, selectedDevice);
    console.log(`Output for ${selectedDevice}:`, data);
  };


  // Initial data fetch
  useEffect(() => {
    (async () => {
      try {
        const [lowDeviceRes, allDeviceRes] = await Promise.all([
          getLowDevices(),
          getAllDevices()
        ]);

        if (Array.isArray(lowDeviceRes?.low_devices)) {
          const enriched = lowDeviceRes.low_devices.map((item, i) => ({
            ...item,
            color: ['secondary', 'primary', 'error', 'success'][i % 4]
          }));
          setLowDeviceData(enriched);
        }

        if (allDeviceRes?.devices && typeof allDeviceRes.devices === 'object') {
          setDeviceList(Object.keys(allDeviceRes.devices));
          setAllDevicesMap(allDeviceRes.devices);
          console.log("Loaded Device Map:", allDeviceRes.devices);
        }
      } catch (err) {
        console.error('Error fetching devices:', err);
        setLowDeviceData([]);
        setDeviceList([]);
        setAllDevicesMap({});
      }
    })();
  }, []);

  // Fetch default order data on date change
  useEffect(() => {
    fetchOrderData(fromDate, toDate, []);
  }, [fromDate, toDate]);

  return (
    <Grid container spacing={2}>
      {/* Order Received Card */}
      <Grid item xs={12} md={6}>
        <Card elevation={0} sx={{ borderRadius: 4, backgroundColor: '#ffffff', height: '100%', display: 'flex', flexDirection: 'column' }}>
          <CardHeader title={<Typography variant="h3" color="primary">Order Received from CSP</Typography>} />
          <CardContent sx={{ pt: 1, flex: 1 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <TextField
                type="date"
                label="From Date"
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 110, maxWidth: 120 }}
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                InputProps={{ sx: { fontSize: '0.75rem', height: 36 } }}
                inputProps={{ style: { fontSize: '0.75rem' } }}
              />
              <TextField
                type="date"
                label="To Date"
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 110, maxWidth: 120 }}
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                InputProps={{ sx: { fontSize: '0.75rem', height: 36 } }}
                inputProps={{ style: { fontSize: '0.75rem' } }}
              />
            </Box>

            {loadingOrders ? (
              <Box sx={{ height: 150, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <CircularProgress size={24} color="primary" />
              </Box>
            ) : (
              <Grid container spacing={1}>
                {orderData.map((item, idx) => (
                  <React.Fragment key={item.name}>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          px: 3,
                          py: 1,
                          borderRadius: 2,
                          border: '1px solid #e0e0e0',
                          backgroundColor: idx % 2 === 0 ? '#f8f9fb' : '#fff',
                          '&:hover': {
                            borderColor: 'primary.main',
                            backgroundColor: '#e3f2fd'
                          }
                        }}
                      >
                        <Grid container justifyContent="space-between" alignItems="center">
                          <Typography
                            variant="subtitle1"
                            fontWeight={600}
                            color={item.name === 'No Orders Found' ? 'error' : 'textPrimary'}
                          >
                            {item.name}
                          </Typography>

                          <Typography
                            variant="h6"
                            color={item.name === 'No Orders Found' ? 'error' : 'primary'}
                          >
                            {item.count}
                          </Typography>
                        </Grid>
                      </Box>
                    </Grid>

                    {idx === 0 && (
                      <Grid item xs={12}>
                        <FormControl fullWidth size="small">
                          <Select
                            value={selectedOption}
                            onChange={handleChange}
                            displayEmpty
                            renderValue={(selected) => {
                              if (!selected || selected === 'stock') {
                                return <Typography sx={{ color: '#aaa' }}>--Device--</Typography>;
                              }
                              return selected;
                            }}
                            input={
                              <OutlinedInput
                                notched={false}
                                sx={{
                                  borderRadius: '2px',
                                  fontWeight: 600,
                                  fontSize: '0.9rem',
                                  backgroundColor: '#f9f9f9',
                                  boxShadow: '0px 2px 6px rgba(0,0,0,0.04)',
                                  '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(0, 0, 0, 0.12)'
                                  },
                                  '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#592375'
                                  },
                                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#592375'
                                  }
                                }}
                              />
                            }
                            sx={{
                              '& .MuiSelect-select': {
                                paddingY: 1.2,
                                paddingX: 2,
                                color: '#000'
                              },
                              '& .MuiSelect-icon': {
                                color: '#592375'
                              }
                            }}
                          >
                            <MenuItem value="stock">--Device--</MenuItem>
                            {deviceList.map((device) => (
                              <MenuItem key={device} value={device}>
                                {device}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    )}
                  </React.Fragment>
                ))}
              </Grid>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Low Device Card */}
      <Grid item xs={12} md={6}>
        <Card elevation={0} sx={{ borderRadius: 4, backgroundColor: theme.palette.background.paper, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <CardHeader title={<Typography variant="h3" color="error">Low Device</Typography>} />
          <CardContent sx={{ pt: 1, flex: 1 }}>
            <Grid container sx={{ mb: 2, borderBottom: '1px solid #e0e0e0', pb: 1 }}>
              <Grid item xs={6}><Typography variant="subtitle2" color="textSecondary">Device Name</Typography></Grid>
              <Grid item xs={3}><Typography variant="subtitle2" color="textSecondary">Count</Typography></Grid>
              <Grid item xs={3} sx={{ textAlign: 'right' }}><Typography variant="subtitle2" color="textSecondary">Sales</Typography></Grid>
            </Grid>

            {lowDeviceData.length > 0 ? (
              lowDeviceData.map((item, index) => {
                const progressValue = Math.min((item.total_sales_count / item.total_count) * 100, 100);
                return (
                  <Box key={item.name} sx={{ px: 1, py: 1.5, borderRadius: 2, backgroundColor: index % 2 === 0 ? '#f9f9f9' : 'transparent', mb: 1 }}>
                    <Grid container alignItems="center" spacing={1}>
                      <Grid item xs={6}><Typography variant="body1" fontWeight={500}>{item.name}</Typography></Grid>
                      <Grid item xs={3}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ flexGrow: 1, minWidth: 100 }}>
                            <LinearProgress variant="determinate" value={progressValue} color={item.color} sx={{ height: 8, borderRadius: 5 }} />
                          </Box>
                          <Typography variant="body2" fontWeight={600} minWidth={20}>{item.total_count}</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={3}>
                        <Box display="flex" justifyContent="flex-end">
                          <Chip label={item.total_sales_count} size="small" color={item.color} sx={{ fontWeight: 500 }} />
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                );
              })
            ) : (
              <Typography variant="body2" color="textSecondary">
                No low devices found.
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}
